import { useMemo } from "react";
import {
  createFileRoute,
  redirect,
  useLocation,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { Building2, Check, Settings as SettingsIcon } from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "beproduct-ui";
import { AppLayout } from "@/components/app-layout";
import { authQueryOptions, companiesQueryOptions, useAuth } from "@/auth/queryOptions";
import { useQuery } from "@tanstack/react-query";
import { buildCompanyNav } from "@/data/nav-items";
import { companyBreadcrumbs } from "@/components/navigation/company-breadcrumbs";

/**
 * Pathless layout that gates every authed page. `beforeLoad` runs on
 * every route under this branch (`_authed/*`) — if `/api/auth/me`
 * returns null we send the browser to /login. AppLayout (our local
 * fork of beproduct-ui's) mounts the sidebar + header and renders the
 * matched child route via its internal <Outlet/>.
 *
 * Reads the active company slug from the matched route tree
 * (anything under `/_authed/$companySlug/*` carries `companySlug`
 * params), then materializes company-scoped sidebar nav + a user-menu
 * Companies switcher.
 */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(authQueryOptions);
    if (!user) {
      throw redirect({ to: "/login", search: { returnTo: location.pathname } });
    }
  },
  component: AuthedShell,
});

function AuthedShell() {
  const { user, logout, adminDomains, idsTenants } = useAuth();
  const matches = useMatches();
  const location = useLocation();
  const nav = useNavigate();

  const companiesQ = useQuery(companiesQueryOptions);
  // Global admin = IDS admin of at least one company. Drives whether
  // we expose Settings in the user-menu (Settings itself also gates
  // server-side, this just keeps the menu clean).
  const isGlobalAdmin = adminDomains.length > 0;

  // Active company for the URL — only set when the current route is
  // company-scoped. Drives the highlighted item in the sidebar.
  const routeCompanySlug = useMemo<string | null>(() => {
    for (const m of matches) {
      const params = m.params as Record<string, string> | undefined;
      if (params && typeof params.companySlug === "string") {
        return params.companySlug;
      }
    }
    return null;
  }, [matches]);

  // Active company for the sidebar — when the route itself doesn't
  // carry a slug (/, /settings) we fall back to `lastCompanySlug` from
  // localStorage so the user keeps a stable nav after popping out to
  // settings. Validated against the user's accessible companies so a
  // stale lastCompanySlug from before a permission revoke can't pin a
  // company they can no longer see.
  const activeCompanySlug = useMemo<string | null>(() => {
    if (routeCompanySlug) return routeCompanySlug;
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem("lastCompanySlug");
    const accessible = companiesQ.data?.companies ?? [];
    if (accessible.find((c) => c.slug === stored)) return stored;
    return accessible[0]?.slug ?? null;
  }, [routeCompanySlug, companiesQ.data]);

  // Trim the company-scoped nav for non-admins. The check is per-company:
  // a user can be admin of company A and not of company B; switching
  // companies re-runs this memo.
  const activeIsAdmin = useMemo(() => {
    if (!activeCompanySlug) return true;
    return (
      companiesQ.data?.companies.find((c) => c.slug === activeCompanySlug)
        ?.isAdmin ?? false
    );
  }, [activeCompanySlug, companiesQ.data]);
  const navItems = useMemo(
    () => buildCompanyNav(activeCompanySlug, { isAdmin: activeIsAdmin }),
    [activeCompanySlug, activeIsAdmin],
  );

  // Only the company in the current URL is the active breadcrumb context.
  // Settings never inherits the last visited company's name.
  const routeCompany = routeCompanySlug
    ? companiesQ.data?.companies.find((c) => c.slug === routeCompanySlug)
    : undefined;
  const routeCompanyName = routeCompany
    ? idsTenants.find((t) => t.domain.toLowerCase() === routeCompany.companyDomain?.toLowerCase())?.name ?? routeCompany.name
    : undefined;
  const breadcrumbs = companyBreadcrumbs(location.pathname, routeCompanySlug, routeCompanyName, location.state);
  const userMenuChildren = (
    <>
      <DropdownMenuLabel className="text-xs text-muted-foreground">
        Companies
      </DropdownMenuLabel>
      {(companiesQ.data?.companies ?? []).length === 0 ? (
        <DropdownMenuItem disabled>
          <Building2 className="mr-2 h-4 w-4" />
          <span className="text-muted-foreground">No companies yet</span>
        </DropdownMenuItem>
      ) : (
        (companiesQ.data?.companies ?? []).map((c) => {
          const isActive = c.slug === activeCompanySlug;
          return (
            <DropdownMenuItem
              key={c.id}
              onSelect={() => {
                if (isActive) return;
                nav({
                  to: "/$companySlug",
                  params: { companySlug: c.slug },
                });
              }}
            >
              <Building2 className="mr-2 h-4 w-4" />
              <span className="flex-1 truncate">{c.name}</span>
              {isActive && <Check className="ml-2 h-4 w-4" />}
            </DropdownMenuItem>
          );
        })
      )}
      {isGlobalAdmin && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => nav({ to: "/settings" })}>
            <SettingsIcon className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  return (
    <AppLayout
      navItems={navItems}
      breadcrumbs={breadcrumbs}
      userProfileProps={{
        name: user?.name ?? user?.email,
        email: user?.email,
        onLogout: () => void logout(),
        children: userMenuChildren,
      }}
    />
  );
}
